import { type FC, type ComponentProps } from 'react';
import { layoutEntry } from '../shared/layoutEntry';
import { graphql, useRefetchableFragment } from 'react-relay';

// components
import { PdpRespUnavailableContainer } from './PdpRespUnavailableContainer/PdpRespUnavailableContainer';
import { PdpRespUnavailableContextProvider } from './PdpRespUnavailableContext/PdpRespUnavailableContext';
import { PdpSharedOriginalLanguageProvider } from '../PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider';

// types
import { type PdpRespUnavailableLayoutEntryQuery } from './__generated__/PdpRespUnavailableLayoutEntryQuery.graphql';
import { type PdpRespUnavailableLayout_viewer$key as Viewer } from './__generated__/PdpRespUnavailableLayout_viewer.graphql';

const viewerFragment = graphql`
    fragment PdpRespUnavailableLayout_viewer on Viewer
    @refetchable(queryName: "PdpRespUnavailableLayoutRefetchQuery") {
        itemRead(itemIds: $itemIds) {
            ... on Item {
                ...PdpRespUnavailableContainer_item
                ...PdpRespUnavailableContextProvider_item
                ...PdpSharedOriginalLanguageProvider_item
            }
        }
        user(userId: $userId) @include(if: $fetchUser) {
            ...PdpRespUnavailableContainer_user
        }
        ...PdpRespUnavailableContainer_viewer
    }
`;

const PdpRespUnavailableLayout: FC<{
    viewer: Viewer;
}> = ({ viewer: viewerRef }) => {
    const [viewer, refetch] = useRefetchableFragment(viewerFragment, viewerRef);
    const { user = null, itemRead } = viewer;
    const item = itemRead?.[0];

    if (!item) {
        return null;
    }

    return (
        <PdpRespUnavailableContextProvider item={item} refetch={refetch}>
            <PdpSharedOriginalLanguageProvider item={item}>
                <PdpRespUnavailableContainer item={item} user={user} viewer={viewer} />
            </PdpSharedOriginalLanguageProvider>
        </PdpRespUnavailableContextProvider>
    );
};

// disabled eslint rule because it's impossible to resolve the warning in this case because unmasked data is used in the route handler
/* eslint relay/unused-fields: "off" */
export const pdpRespUnavailableLayoutEntry = layoutEntry<
    ComponentProps<typeof PdpRespUnavailableLayout>,
    PdpRespUnavailableLayoutEntryQuery
>(
    PdpRespUnavailableLayout,
    graphql`
        query PdpRespUnavailableLayoutEntryQuery(
            $userId: String!
            $itemId: String!
            $isTrade: Boolean!
            $itemIds: [String]!
            $fetchUser: Boolean!
            $fetchChat: Boolean!
            $guestId: String = ""
            $afterDisplay: Boolean!
            $afterDisplayAndBtSsr: Boolean!
            $afterDisplayAndBtNone: Boolean!
            $userZipCode: String = ""
            $forcedCurrency: Currency
            $priceBookName: String = ""
            $userCountryCode: String = ""
            $numOfSponsoredItems: Int = 30
            $maxSponsoredItems: Int
            $minSponsoredItemsSteps: [Int!]
        ) {
            viewer {
                ...PdpRespUnavailableLayout_viewer
                ...PdpHeadBuilder_viewer
                itemRead(itemIds: $itemIds) {
                    ...findPdpItemReadError_itemRead
                    ... on Item {
                        isAuctionEnabled
                        isSold
                        ...PdpHeadBuilder_item
                        ...handlePdpStatuses_item @skip(if: $afterDisplay)
                    }
                }
            }
        }
    `
);
